import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { computeData, buildSteps } from './helpers/frameLogic';
import { DEFAULT_TIERS, RAW_COSTS_CONFIG } from './helpers/frameConstants';
import { HangingType } from './helpers/segFrameBuilderTypes';
export const useFrameStore = defineStore('frameStore', () => {
    // ── STATE (refs) ──────────────────────────────────────────
    const activeFrame = ref({
        dims: { w: 100, h: 100 },
        frameColor: 'silver',
        pricingTier: 'retail',
        quantity: 1,
        hangingOption: HangingType.CLIPS
    });
    const frameCollection = ref([]);
    // Initializing costs from constants
    const rawCosts = ref(Object.fromEntries(RAW_COSTS_CONFIG.map(c => [c.id, c.def])));
    const tierPrices = ref(JSON.parse(JSON.stringify(DEFAULT_TIERS)));
    const activeTab = ref('production');
    const showCostSettings = ref(false);
    const viewport = ref({ zoom: 1, panX: 0, panY: 0 });
    const isExploded = ref(false);
    const assemblyStep = ref(0);
    const isInternalPrintView = ref(false);
    // ── GETTERS (computed) ────────────────────────────────────
    const activeFrameData = computed(() => {
        // We pass the current state values into our pure helper function
        return computeData({
            dims: activeFrame.value.dims,
            rawCosts: rawCosts.value,
            tierPrices: tierPrices.value,
            pricingTier: activeFrame.value.pricingTier,
            quantity: activeFrame.value.quantity,
            hangingOption: activeFrame.value.hangingOption
        });
    });
    const assemblySteps = computed(() => {
        return activeFrameData.value ? buildSteps(activeFrameData.value) : [];
    });
    const batchHardwareSummary = computed(() => {
        return frameCollection.value.reduce((acc, frame) => {
            const data = computeData({
                ...frame,
                rawCosts: rawCosts.value,
                tierPrices: tierPrices.value
            });
            if (data) {
                acc.screws += data.hw.screws * frame.quantity;
                acc.anchors += data.hw.anchors * frame.quantity;
                acc.corners += data.hw.cornerConnectors * frame.quantity;
            }
            return acc;
        }, { screws: 0, anchors: 0, corners: 0 });
    });
    // ── ACTIONS (functions) ───────────────────────────────────
    function stashCurrentFrame() {
        // Create a deep copy of the active frame
        const snapshot = JSON.parse(JSON.stringify(activeFrame.value));
        const newId = typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `id-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        frameCollection.value.push({
            ...snapshot,
            id: newId
        });
        // Reset builder for next input
        activeFrame.value.dims = { w: 100, h: 100 };
    }
    function removeFrame(id) {
        frameCollection.value = frameCollection.value.filter(f => f.id !== id);
    }
    function updateQuantity(newQty) {
        activeFrame.value.quantity = newQty;
    }
    // Explicitly return everything the app needs to see
    return {
        activeFrame,
        frameCollection,
        rawCosts,
        tierPrices,
        activeTab,
        showCostSettings,
        viewport,
        isExploded,
        assemblyStep,
        isInternalPrintView,
        activeFrameData,
        assemblySteps,
        batchHardwareSummary,
        stashCurrentFrame,
        removeFrame,
        updateQuantity,
    };
});
